import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import PropertyCard, { type PropertyCardData } from '../../components/property/PropertyCard';
import QRCodeModal from '../../components/ui/QRCodeModal';

interface CompanyData {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  logo_url: string | null;
  banner_url: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  city: string | null;
}

interface CompanyResponse {
  data: CompanyData;
}

interface PropertiesResponse {
  data: PropertyCardData[];
  meta: {
    total: number;
    page: number;
    per_page: number;
    total_pages: number;
  };
}

interface Filters {
  property_type: string;
  operation_type: string;
  price_min: string;
  price_max: string;
  city: string;
  bedrooms: string;
}

const PROPERTY_TYPES = ['house', 'apartment', 'land', 'commercial', 'office', 'warehouse', 'other'];
const OPERATION_TYPES = ['sale', 'rent', 'temporary_rent'];

export default function CatalogPage(): React.ReactElement {
  const { t } = useTranslation('catalog');
  const { companySlug } = useParams<{ companySlug: string }>();
  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<Filters>({
    property_type: '',
    operation_type: '',
    price_min: '',
    price_max: '',
    city: '',
    bedrooms: '',
  });
  const [appliedFilters, setAppliedFilters] = useState<Filters>(filters);
  const [page, setPage] = useState(1);
  const [allProperties, setAllProperties] = useState<PropertyCardData[]>([]);
  const [qrOpen, setQrOpen] = useState(false);

  // Fetch company data
  const { data: companyData, isLoading: companyLoading, error: companyError } = useQuery({
    queryKey: ['catalog-company', companySlug],
    queryFn: () => api.get<CompanyResponse>(`/v1/public/catalog/${companySlug}`),
    enabled: !!companySlug,
  });

  const company = companyData?.data;

  // Build query params from applied filters
  const buildParams = (p: number): Record<string, string | number> => {
    const params: Record<string, string | number> = { page: p, per_page: 12 };
    if (appliedFilters.property_type) params.property_type = appliedFilters.property_type;
    if (appliedFilters.operation_type) params.operation_type = appliedFilters.operation_type;
    if (appliedFilters.price_min) params.price_min = appliedFilters.price_min;
    if (appliedFilters.price_max) params.price_max = appliedFilters.price_max;
    if (appliedFilters.city) params.city = appliedFilters.city;
    if (appliedFilters.bedrooms) params.bedrooms = appliedFilters.bedrooms;
    return params;
  };

  // Fetch properties
  const { data: propertiesData, isLoading: propertiesLoading } = useQuery({
    queryKey: ['catalog-properties', companySlug, appliedFilters, page],
    queryFn: async () => {
      const response = await api.get<PropertiesResponse>(
        `/v1/public/catalog/${companySlug}/properties`,
        buildParams(page)
      );
      if (page === 1) {
        setAllProperties(response.data);
      } else {
        setAllProperties((prev) => [...prev, ...response.data]);
      }
      return response;
    },
    enabled: !!companySlug,
  });

  const meta = propertiesData?.meta;

  // Fetch user favorites (if authenticated)
  const { data: favoritesData } = useQuery({
    queryKey: ['user-favorites'],
    queryFn: () => api.get<{ data: Array<{ property_id: number }> }>('/v1/favorites'),
    enabled: isAuthenticated,
  });

  const favoriteIds = new Set(favoritesData?.data?.map((f) => f.property_id) ?? []);

  // Toggle favorite mutation
  const favoriteMutation = useMutation({
    mutationFn: async (propertyId: number) => {
      if (favoriteIds.has(propertyId)) {
        return api.delete(`/v1/favorites/${propertyId}`);
      }
      return api.post('/v1/favorites', { property_id: propertyId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-favorites'] });
    },
  });

  const handleApplyFilters = (): void => {
    setPage(1);
    setAllProperties([]);
    setAppliedFilters({ ...filters });
  };

  const handleClearFilters = (): void => {
    const cleared: Filters = {
      property_type: '',
      operation_type: '',
      price_min: '',
      price_max: '',
      city: '',
      bedrooms: '',
    };
    setFilters(cleared);
    setPage(1);
    setAllProperties([]);
    setAppliedFilters(cleared);
  };

  const handleLoadMore = (): void => {
    setPage((prev) => prev + 1);
  };

  const catalogUrl = `${window.location.origin}/c/${companySlug}`;

  // Error state
  if (companyError) {
    const err = companyError as unknown as ApiError;
    return (
      <div>
        <div className="navbar bg-base-100 shadow-sm">
          <div className="flex-1 gap-2">
            <span className="text-xl">&#127970;</span>
            <span className="text-lg font-semibold">{t('title')}</span>
          </div>
        </div>
        <div className="max-w-screen-lg mx-auto px-4 mt-8 text-center">
          <div className="alert alert-error mb-4">
            <span>{err.status === 404 ? t('company.notFound') : t('company.notFoundDescription')}</span>
          </div>
          <RouterLink to="/" className="btn btn-outline">
            {t('company.notFound')}
          </RouterLink>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200">
      {/* Navbar with company name */}
      <div className="navbar bg-base-100 shadow-sm">
        <div className="flex-1 gap-2">
          {company?.logo_url ? (
            <div className="avatar">
              <div className="w-9 rounded-full">
                <img src={company.logo_url} alt={company.name} />
              </div>
            </div>
          ) : (
            <span className="text-xl">&#127970;</span>
          )}
          <span className="text-lg font-semibold">
            {companyLoading ? (
              <span className="skeleton w-48 h-6 inline-block" />
            ) : (
              company?.name
            )}
          </span>
        </div>
      </div>

      {/* Company banner */}
      {company?.banner_url && (
        <div
          className="w-full h-[150px] md:h-[250px] bg-cover bg-center"
          style={{ backgroundImage: `url(${company.banner_url})` }}
        />
      )}

      <div className="max-w-screen-lg mx-auto px-4 py-8">
        {/* Company header */}
        {companyLoading ? (
          <div className="mb-8">
            <div className="skeleton h-10 w-72 mb-2" />
            <div className="skeleton h-5 w-96" />
          </div>
        ) : company ? (
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-2">
              {company.logo_url && (
                <div className="avatar">
                  <div className="w-16 rounded-full">
                    <img src={company.logo_url} alt={company.name} />
                  </div>
                </div>
              )}
              <div>
                <h1 className="text-3xl font-bold">{company.name}</h1>
                {company.city && (
                  <p className="text-sm text-base-content/60">{company.city}</p>
                )}
              </div>
              <button
                className="btn btn-outline btn-sm ml-auto"
                onClick={() => setQrOpen(true)}
                title={t('share.qr')}
              >
                &#9641; {t('share.qr')}
              </button>
            </div>
            {company.description && (
              <p className="text-base-content/70 mb-3">{company.description}</p>
            )}
            <div className="flex flex-wrap gap-2">
              {company.phone && (
                <span className="badge badge-outline badge-sm">{t('company.phone')}: {company.phone}</span>
              )}
              {company.email && (
                <span className="badge badge-outline badge-sm">{t('company.email')}: {company.email}</span>
              )}
              {company.website && (
                <span className="badge badge-outline badge-sm">{t('company.website')}: {company.website}</span>
              )}
            </div>
          </div>
        ) : null}

        {/* Filter bar */}
        <div className="bg-base-100 rounded-lg shadow-sm p-4 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-base-content/60">&#9776;</span>
            <span className="font-semibold">{t('filters.title')}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3">
            <div className="form-control">
              <select
                className="select select-bordered select-sm w-full"
                value={filters.property_type}
                onChange={(e) => setFilters((f) => ({ ...f, property_type: e.target.value }))}
              >
                <option value="">{t('filters.propertyType')} - {t('filters.allTypes')}</option>
                {PROPERTY_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`propertyTypes.${type}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-control">
              <select
                className="select select-bordered select-sm w-full"
                value={filters.operation_type}
                onChange={(e) => setFilters((f) => ({ ...f, operation_type: e.target.value }))}
              >
                <option value="">{t('filters.operationType')} - {t('filters.allOperations')}</option>
                {OPERATION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`operationTypes.${type}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-control">
              <input
                type="number"
                className="input input-bordered input-sm w-full"
                placeholder={t('filters.priceMin')}
                value={filters.price_min}
                onChange={(e) => setFilters((f) => ({ ...f, price_min: e.target.value }))}
              />
            </div>
            <div className="form-control">
              <input
                type="number"
                className="input input-bordered input-sm w-full"
                placeholder={t('filters.priceMax')}
                value={filters.price_max}
                onChange={(e) => setFilters((f) => ({ ...f, price_max: e.target.value }))}
              />
            </div>
            <div className="form-control">
              <input
                type="text"
                className="input input-bordered input-sm w-full"
                placeholder={t('filters.city')}
                value={filters.city}
                onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
              />
            </div>
            <div className="form-control">
              <input
                type="number"
                className="input input-bordered input-sm w-full"
                placeholder={t('filters.bedrooms')}
                value={filters.bedrooms}
                onChange={(e) => setFilters((f) => ({ ...f, bedrooms: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button className="btn btn-primary btn-sm" onClick={handleApplyFilters}>
              {t('filters.apply')}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={handleClearFilters}>
              {t('filters.clear')}
            </button>
          </div>
        </div>

        {/* Properties grid */}
        {propertiesLoading && allProperties.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i}>
                <div className="skeleton h-[200px] w-full rounded-t-lg" />
                <div className="skeleton h-8 w-full mt-2" />
                <div className="skeleton h-5 w-3/5 mt-1" />
              </div>
            ))}
          </div>
        ) : allProperties.length === 0 ? (
          <div className="text-center py-16">
            <h3 className="text-lg font-semibold text-base-content/60">
              {t('pagination.noResults')}
            </h3>
            <p className="text-sm text-base-content/50">
              {t('pagination.noResultsDescription')}
            </p>
          </div>
        ) : (
          <>
            {meta && (
              <p className="text-sm text-base-content/60 mb-4">
                {t('pagination.showing', { count: allProperties.length, total: meta.total })}
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {allProperties.map((property) => (
                <PropertyCard
                  key={property.id}
                  property={property}
                  companySlug={companySlug}
                  showFavorite={isAuthenticated}
                  isFavorite={favoriteIds.has(property.id)}
                  onToggleFavorite={(id) => favoriteMutation.mutate(id)}
                />
              ))}
            </div>

            {/* Load more */}
            {meta && meta.page < meta.total_pages && (
              <div className="text-center mt-8">
                <button
                  className="btn btn-outline"
                  onClick={handleLoadMore}
                  disabled={propertiesLoading}
                >
                  {propertiesLoading ? (
                    <span className="loading loading-spinner loading-sm" />
                  ) : (
                    t('pagination.loadMore')
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* QR Code modal */}
      <QRCodeModal
        url={catalogUrl}
        title={company?.name || t('title')}
        isOpen={qrOpen}
        onClose={() => setQrOpen(false)}
      />
    </div>
  );
}
